import React, { useEffect, useState } from 'react';
import {
    AiCategorizationFields,
    FeedbackCategory,
    FeedbackSentiment,
    FeedbackUrgency,
    CATEGORY_LABELS,
    SENTIMENT_LABELS,
    URGENCY_LABELS,
    URGENCY_COLORS,
    SENTIMENT_COLORS,
} from './feedbackTypes';

interface FeedbackSubmissionItem extends AiCategorizationFields {
    bugReportId: string;
    createdAt: string;
    description: string;
    feedbackType?: string;
}

interface PromptResponseItem extends AiCategorizationFields {
    responseId: string;
    createdAt: string;
    triggerId: string;
    followUpText: string | null;
}

type NeedsAttentionItem = {
    id: string;
    createdAt: string;
    source: 'Feedback' | 'Prompt';
    text: string;
    urgency: FeedbackUrgency;
};

export type FeedbackInsightsSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

// Aggregates AI category/sentiment/urgency across both feedback sources
// (the feedback table's free-form submissions and FeedbackPromptResponsesTable's
// milestone-prompt responses). Fetches every page client-side and aggregates
// in the browser — same approach MetricsSection already uses for user counts.
export const FeedbackInsightsSection: React.FC<FeedbackInsightsSectionProps> = ({ API_BASE, authHeaders }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalSubmissions, setTotalSubmissions] = useState(0);
    const [categoryCounts, setCategoryCounts] = useState<Record<FeedbackCategory, number>>({} as any);
    const [sentimentCounts, setSentimentCounts] = useState<Record<FeedbackSentiment, number>>({} as any);
    const [urgencyCounts, setUrgencyCounts] = useState<Record<FeedbackUrgency, number>>({} as any);
    const [triggerCounts, setTriggerCounts] = useState<Record<string, number>>({});
    const [uncategorizedCount, setUncategorizedCount] = useState(0);
    const [needsAttention, setNeedsAttention] = useState<NeedsAttentionItem[]>([]);

    const fetchAllPages = async <T,>(path: string): Promise<T[]> => {
        let items: T[] = [];
        let lastKey: string | null = null;
        do {
            const url: string = lastKey
                ? `${API_BASE}${path}?limit=100&lastKey=${encodeURIComponent(lastKey)}`
                : `${API_BASE}${path}?limit=100`;
            const res: Response = await fetch(url, { headers: authHeaders() });
            if (!res.ok) {
                const err: any = await res.json().catch(() => ({}));
                throw new Error(err?.message || `Failed to fetch ${path}.`);
            }
            const data: any = await res.json();
            items = items.concat(Array.isArray(data.items) ? data.items : []);
            lastKey = data.lastKey || null;
        } while (lastKey);
        return items;
    };

    const fetchInsights = async () => {
        setLoading(true);
        setError(null);
        try {
            const [feedbackItems, promptResponses] = await Promise.all([
                fetchAllPages<FeedbackSubmissionItem>('/feedback'),
                fetchAllPages<PromptResponseItem>('/feedback-prompts/responses'),
            ]);

            const categories: Record<string, number> = {};
            const sentiments: Record<string, number> = {};
            const urgencies: Record<string, number> = {};
            const triggers: Record<string, number> = {};
            const attention: NeedsAttentionItem[] = [];
            let uncategorized = 0;

            const tally = (item: AiCategorizationFields, id: string, createdAt: string, text: string, source: 'Feedback' | 'Prompt') => {
                if (!item.aiCategory) {
                    uncategorized += 1;
                    return;
                }
                categories[item.aiCategory] = (categories[item.aiCategory] || 0) + 1;
                if (item.aiSentiment) sentiments[item.aiSentiment] = (sentiments[item.aiSentiment] || 0) + 1;
                if (item.aiUrgency) {
                    urgencies[item.aiUrgency] = (urgencies[item.aiUrgency] || 0) + 1;
                    if (item.aiUrgency === 'high') {
                        attention.push({ id, createdAt, source, text: item.aiSummary || text, urgency: item.aiUrgency });
                    }
                }
            };

            for (const item of feedbackItems) {
                tally(item, item.bugReportId, item.createdAt, item.description, 'Feedback');
            }
            for (const response of promptResponses) {
                triggers[response.triggerId] = (triggers[response.triggerId] || 0) + 1;
                if (response.followUpText) {
                    tally(response, response.responseId, response.createdAt, response.followUpText, 'Prompt');
                }
            }

            attention.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

            setTotalSubmissions(feedbackItems.length + promptResponses.length);
            setCategoryCounts(categories as Record<FeedbackCategory, number>);
            setSentimentCounts(sentiments as Record<FeedbackSentiment, number>);
            setUrgencyCounts(urgencies as Record<FeedbackUrgency, number>);
            setTriggerCounts(triggers);
            setUncategorizedCount(uncategorized);
            setNeedsAttention(attention.slice(0, 10));
        } catch (err: any) {
            setError(err.message || 'Failed to load feedback insights.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const BarRow: React.FC<{ label: string; count: number; total: number; color?: string }> = ({ label, count, total, color }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <span style={{ width: 130, fontSize: '0.85rem', color: '#444' }}>{label}</span>
            <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', height: 14 }}>
                <div
                    style={{
                        width: total > 0 ? `${Math.max((count / total) * 100, count > 0 ? 2 : 0)}%` : '0%',
                        background: color || '#4f46e5',
                        height: '100%',
                    }}
                />
            </div>
            <span style={{ width: 36, textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>{count}</span>
        </div>
    );

    const categoryTotal = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
    const sentimentTotal = Object.values(sentimentCounts).reduce((a, b) => a + b, 0);
    const urgencyTotal = Object.values(urgencyCounts).reduce((a, b) => a + b, 0);
    const triggerTotal = Object.values(triggerCounts).reduce((a, b) => a + b, 0);

    return (
        <section className="table-section" style={{ maxWidth: '100%' }}>
            <h2>Feedback Insights</h2>
            {loading && <p className="status">Loading insights...</p>}
            {error && <p className="status status-error" style={{ color: 'red' }}>{error}</p>}

            {!loading && !error && (
                <>
                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.25rem' }}>
                        <li><strong>Total Submissions:</strong> {totalSubmissions}</li>
                        <li><strong>Categorized by AI:</strong> {categoryTotal} &nbsp;·&nbsp; <strong>Not yet categorized:</strong> {uncategorizedCount}</li>
                    </ul>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>By Category</h3>
                            {(Object.keys(CATEGORY_LABELS) as FeedbackCategory[]).map((cat) => (
                                <BarRow key={cat} label={CATEGORY_LABELS[cat]} count={categoryCounts[cat] || 0} total={categoryTotal} />
                            ))}
                        </div>
                        <div>
                            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>By Sentiment</h3>
                            {(Object.keys(SENTIMENT_LABELS) as FeedbackSentiment[]).map((s) => (
                                <BarRow key={s} label={SENTIMENT_LABELS[s]} count={sentimentCounts[s] || 0} total={sentimentTotal} color={SENTIMENT_COLORS[s]} />
                            ))}
                        </div>
                        <div>
                            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>By Urgency</h3>
                            {(Object.keys(URGENCY_LABELS) as FeedbackUrgency[]).map((u) => (
                                <BarRow key={u} label={URGENCY_LABELS[u]} count={urgencyCounts[u] || 0} total={urgencyTotal} color={URGENCY_COLORS[u]} />
                            ))}
                        </div>
                        <div>
                            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>By Milestone Prompt</h3>
                            {Object.keys(triggerCounts).length === 0 && <p style={{ color: '#999', fontSize: '0.85rem' }}>No prompt responses yet.</p>}
                            {Object.entries(triggerCounts).map(([trigger, count]) => (
                                <BarRow key={trigger} label={trigger} count={count} total={triggerTotal} color="#0ea5e9" />
                            ))}
                        </div>
                    </div>

                    <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>Needs Attention (high urgency)</h3>
                    {needsAttention.length === 0 ? (
                        <p style={{ color: '#999', fontSize: '0.85rem' }}>Nothing flagged as high urgency right now.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {needsAttention.map((item) => (
                                <li
                                    key={item.id}
                                    style={{
                                        padding: '8px 12px',
                                        marginBottom: 6,
                                        borderLeft: `3px solid ${URGENCY_COLORS.high}`,
                                        background: '#fafafa',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <span style={{ color: '#999', marginRight: 8 }}>[{item.source}]</span>
                                    <span style={{ color: '#999', marginRight: 8 }}>{new Date(item.createdAt).toLocaleDateString()}</span>
                                    {item.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
};
